import React, { useState, useEffect, useRef } from 'react';
import { SuccessCircle, WarningIcon } from '../../assets/icons';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';
import { sendAnswer, getRequesCategories, getHazirCevaplar, getHazirCevapImageUrl } from "../../services/requestService";

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

const normalizeName = (value) => (value || '').trim().toLocaleLowerCase('tr-TR');

// Talep kaydındaki kategori adı, kayıtlı kategorinin adından farklı olabiliyor.
const KATEGORI_ADI_ESLESMELERI = {
  [normalizeName('Poşetim doldu/Poşet talebi')]: normalizeName('Poşetim Doldu/Yırtıldı/Kaybettim'),
};

const resolveKategoriAdi = (value) => {
  const normalized = normalizeName(value);
  return KATEGORI_ADI_ESLESMELERI[normalized] || normalized;
};

const SendAnswerModal = ({ isOpen, onClose, onSuccess, selectedRequestId, kategoriAdi }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    text: '',
    image: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);
  const [hazirCevaplar, setHazirCevaplar] = useState([]);
  const [hazirCevapStatus, setHazirCevapStatus] = useState('idle');
  const [selectedHazirCevapId, setSelectedHazirCevapId] = useState('');
  const [hazirCevapImageUrl, setHazirCevapImageUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Talep listesi kategori id'si döndürmüyor; kategori adıyla eşleştiriyoruz.
  useEffect(() => {
    if (!isOpen || !kategoriAdi) return;
    let cancelled = false;
    const fetchHazirCevaplar = async () => {
      setHazirCevapStatus('loading');
      try {
        const categories = await getRequesCategories();
        const category = (categories.data || []).find(c => normalizeName(c.adi) === resolveKategoriAdi(kategoriAdi));
        if (!category) {
          if (!cancelled) setHazirCevapStatus('noCategory');
          return;
        }
        const list = await getHazirCevaplar(category.id);
        if (!cancelled) {
          setHazirCevaplar(list);
          setHazirCevapStatus('ready');
        }
      } catch (err) {
        console.error('Hazır cevaplar yüklenemedi:', err);
        if (!cancelled) setHazirCevapStatus('error');
      }
    };
    fetchHazirCevaplar();
    return () => { cancelled = true; };
  }, [isOpen, kategoriAdi]);
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(STEPS.FORM);
      setIsLoading(false);
      setError(null);
      setFormData({ text: '', image: null });
      setPreviewImage(null);
      setSubmittedData(null);
      setHazirCevaplar([]);
      setHazirCevapStatus('idle');
      setSelectedHazirCevapId('');
      setHazirCevapImageUrl(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setCurrentStep(STEPS.FORM);
    setIsLoading(false);
    setError(null);
    setFormData({ text: '', image: null });
    setPreviewImage(null);
    setSubmittedData(null);
    setSelectedHazirCevapId('');
    setHazirCevapImageUrl(null);
    onClose();
  };

  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleHazirCevapSelect = (hazirCevap) => {
    setError(null);
    setPreviewImage(null);
    clearFileInput();
    if (String(hazirCevap.id) === selectedHazirCevapId) {
      setSelectedHazirCevapId('');
      setHazirCevapImageUrl(null);
      setFormData(prev => ({
        text: prev.text === (hazirCevap.cevap || '') ? '' : prev.text,
        image: null
      }));
      return;
    }
    setSelectedHazirCevapId(String(hazirCevap.id));
    setFormData({ text: hazirCevap.cevap || '', image: null });
    setHazirCevapImageUrl(getHazirCevapImageUrl(hazirCevap.resim));
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setPreviewImage(null);
    setHazirCevapImageUrl(null);
    clearFileInput();
  };

  const displayedImage = previewImage || hazirCevapImageUrl;

  const handleTextChange = (e) => {
    setFormData(prev => ({
      ...prev,
      text: e.target.value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Resim boyutu 5MB\'dan büyük olamaz.');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    if (!formData.text.trim()) {
      setError('Lütfen cevap alanını doldurunuz.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (validateForm()) {
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.FORM);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
  
    try {
      // API çağrısı
      await sendAnswer(selectedRequestId, {
        text: formData.text,
        image: formData.image,
        hazirCevapId: hazirCevapImageUrl ? selectedHazirCevapId : null
      });
  
      // Başarılıysa:
      setSubmittedData({
        text: formData.text,
        imageUrl: displayedImage
      });
      setCurrentStep(STEPS.COMPLETE);
      onSuccess();            // üst bileşene bildir
    } catch (err) {
      setError("Gönderim sırasında bir hata oluştu.");
      console.error(err);
      setCurrentStep(STEPS.COMPLETE);
    } finally {
      setIsLoading(false);
    }
  };
  const renderStepIndicator = () => (
    <div className="stepper flex justify-between mb-6">
      <div className={`step flex flex-col items-center ${currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center">
          {currentStep >= STEPS.FORM && <div className='step-active-circle w-5 h-5 bg-blue-500 rounded-full'/>}
        </div>
        <span className="step-title text-sm mt-2">Bilgi Girişi</span>
      </div>
      <div className={`step flex flex-col items-center ${currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center">
          {currentStep >= STEPS.CONFIRM && <div className='step-active-circle w-5 h-5 bg-blue-500 rounded-full'/>}
        </div>
        <span className="step-title text-sm mt-2">Bilgi Onayı</span>
      </div>
      <div className={`step flex flex-col items-center ${currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center">
          {currentStep >= STEPS.COMPLETE && <div className='step-active-circle w-5 h-5 bg-blue-500 rounded-full'/>}
        </div>
        <span className="step-title text-sm mt-2">Tamamlandı</span>
      </div>
    </div>
  );

  const renderHazirCevapPicker = () => {
    if (hazirCevapStatus === 'loading') {
      return <div className="ha-picker__hint">Hazır cevaplar yükleniyor...</div>;
    }
    if (hazirCevapStatus === 'noCategory') {
      return <div className="ha-picker__hint">Talebin kategorisi kayıtlı kategorilerle eşleşmedi; hazır cevap gösterilemiyor.</div>;
    }
    if (hazirCevapStatus === 'error') {
      return <div className="ha-picker__hint ha-picker__hint--error">Hazır cevaplar yüklenemedi.</div>;
    }
    if (hazirCevaplar.length === 0) return null;
    return (
      <div className="ha-picker">
        <div className="ha-picker__label">
          Hazır cevaplar
          <span>Birini seçin ya da aşağıya kendiniz yazın</span>
        </div>
        <div className="ha-picker__list">
          {hazirCevaplar.map((h) => {
            const selected = String(h.id) === selectedHazirCevapId;
            return (
              <button
                key={h.id}
                type="button"
                className={`ha-card ${selected ? 'ha-card--selected' : ''}`}
                onClick={() => handleHazirCevapSelect(h)}
                aria-pressed={selected}
              >
                {h.resim && <img className="ha-card__image" src={getHazirCevapImageUrl(h.resim)} alt="" />}
                <span className="ha-card__text">{h.cevap}</span>
                <span className="ha-card__check">{selected ? '✓' : ''}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className="create-response-form send-answer-form">
      {renderHazirCevapPicker()}
      <div className="ha-field">
        <label htmlFor="text">Cevap</label>
        <textarea
          id="text"
          name="text"
          maxLength={3000}
          rows={6}
          value={formData.text}
          onChange={handleTextChange}
          placeholder="Cevabınızı buraya yazın..."
        />
      </div>
      <div className="ha-field">
        <span className="ha-field__label">Resim</span>
        <div className="ha-image">
          {displayedImage && <img src={displayedImage} alt="Önizleme" />}
          <label className="ha-file-button">
            {displayedImage ? 'Değiştir' : 'Resim ekle (isteğe bağlı)'}
            <input ref={fileInputRef} type="file" id="image" name="image" accept="image/*" onChange={handleImageChange} hidden />
          </label>
          {displayedImage && (
            <button type="button" className="ha-link-button" onClick={handleRemoveImage}>
              Kaldır
            </button>
          )}
        </div>
      </div>
      {error && <div className="ha-error">{error}</div>}
      <div className="ha-actions">
        <button type="button" onClick={handleSubmit} className="ha-submit">
          Gönder
        </button>
      </div>
    </div>
  );

  const renderConfirmation = () => (
    <div className="confirmation-step">
      <WarningIcon />
      <h3 className="text-lg font-semibold mb-4">Bilgileri göndermek üzeresiniz. Onaylıyor musunuz?</h3>
      <div className="table-container mb-4">
        <table className="users-table w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 text-left">Cevap</th>
              <th className="border p-2 text-left">Resim</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border p-2">{formData.text}</td>
              <td className="border p-2">
                {displayedImage ? (
                  <img  style={{ width: "120px"}} src={displayedImage} alt="Preview" className="max-w-xs h-auto rounded-md" />
                ) : 'Resim yüklenmedi'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-actions flex gap-2">
        <button type="button" onClick={handleBack} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400">
          İptal Et
        </button>
        <button type="button" onClick={handleConfirm} className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">
          Onayla
        </button>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="complete-step">
      {isLoading ? (
        <div className="loading-state flex flex-col items-center">
          <Spinner size="large" />
          <h3 className="text-lg font-semibold mt-4">Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="bg-red-500 text-white px-4 py-2 rounded-md mt-4 hover:bg-red-600">
            Süreci İptal Et
          </button>
        </div>
      ) : submittedData ? (
        <div className="success-state flex flex-col items-center">
          <SuccessCircle />
          <h3 className="text-lg font-semibold mt-4">Süreciniz tamamlandı. Cevap gönderildi.</h3>
          <div className="table-container mt-4 w-full">
            <table className="users-table w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Cevap</th>
                  <th className="border p-2 text-left">Resim</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-2">{submittedData.text}</td>
                  <td className="border p-2">
                    {submittedData.imageUrl ? (
                      <img src={submittedData.imageUrl} alt="Submitted" className="max-w-xs h-auto rounded-md" />
                    ) : 'Resim yüklenmedi'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" onClick={handleClose} className="bg-blue-500 text-white px-4 py-2 rounded-md mt-4 hover:bg-blue-600">
            Kapat
          </button>
        </div>
      ) : (
        <div className="error-state flex flex-col items-center">
          <div className="error-icon text-red-500 text-4xl">✕</div>
          <h3 className="text-lg font-semibold mt-4">Hata Oluştu</h3>
          <p className="text-red-500">{error}</p>
          <button type="button" onClick={handleBack} className="bg-blue-500 text-white px-4 py-2 rounded-md mt-4 hover:bg-blue-600">
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );

  useModalClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="modal-content bg-white rounded-lg p-6 max-w-2xl w-full">
        <div className="modal-header mb-4">
          <h3 className="text-xl font-semibold">Cevap Gönder</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {currentStep === STEPS.FORM && renderForm()}
          {currentStep === STEPS.CONFIRM && renderConfirmation()}
          {currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default SendAnswerModal;